import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import KPI from "../components/KPI";
import DemoBadge from "../components/DemoBadge";
import { Button } from "../components/ui/button";
import { getImage } from "../brand/images";
import {
  IconCyber, IconInfrastructure, IconGovernment, IconBudget,
  IconWater, IconAI, IconDataMonitor, IconCompliance,
} from "../brand/icons";
import { dashApi } from "../lib/api";
import { ArrowRight } from "lucide-react";

const heroImg = getImage("cyber-soc-defense-center");
const secImg  = getImage("dash-executive-analytics");
const waterImg = getImage("water-data-center-cooling");

const Home = () => {
  const [summary, setSummary] = useState(null);
  useEffect(() => { dashApi.summary().then(setSummary).catch(() => {}); }, []);

  return (
    <Layout>
      <SEOHead
        title="AuditIQ — Government Budget Transparency & Infrastructure Cybersecurity"
        description="AuditIQ is a GovTech accountability platform: AI-driven municipal budget analysis, infrastructure risk dashboards, and the CyberWater Security Layer protecting critical water infrastructure."
        path="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden tech-gradient-bg">
        <div className="absolute inset-0 cyber-grid opacity-60 pointer-events-none" />
        <div className="container-wide pt-12 sm:pt-16 pb-16 sm:pb-24 relative grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-14 items-center">
          <div className="fade-up">
            <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-electric mb-5 flex-wrap">
              <span className="inline-block w-8 h-px bg-electric" /> AuditIQ Intelligence Engine
              <span className="pulse-dot" />
              <span className="text-cream-dim">9 engines · live</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-5xl lg:text-[3rem] xl:text-[3rem] leading-[1.05]">
              Public-Sector <span className="text-gold">Accountability</span><br/>
              at the Speed of Infrastructure.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-cream-dim max-w-2xl leading-relaxed">
              AuditIQ unifies AI-powered budget analysis, infrastructure risk scoring,
              and the CyberWater Security Layer — defending municipal water systems,
              data-center cooling loops, and critical operational technology with
              executive-grade transparency.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 btn-stack">
              <Link to="/demo" className="contents"><Button className="btn-gold h-12 px-6 text-[15px] w-full sm:w-auto" data-testid="cta-launch-demo">
                Launch Interactive Demo <ArrowRight size={16} className="ml-2" />
              </Button></Link>
              <Link to="/video-demo" className="contents"><Button className="btn-ghost-gold h-12 px-6 text-[15px] w-full sm:w-auto" data-testid="cta-video-demo">
                Watch Product Video
              </Button></Link>
              <Link to="/contact" className="contents"><Button variant="ghost" className="h-12 px-6 text-[15px] text-electric hover:text-cream hover:bg-navy-soft w-full sm:w-auto" data-testid="cta-pilot">
                Request Pilot →
              </Button></Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-xs text-steel">
              <span>· Defensive cybersecurity only</span>
              <span>· Public-data compliant crawler</span>
              <span>· Non-accusatory analysis framing</span>
            </div>
          </div>

          <div className="relative fade-in">
            <div className="glass overflow-hidden relative">
              <img src={heroImg.src} alt={heroImg.alt} className="w-full aspect-[16/10] object-cover opacity-75" />
              <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--auditiq-navy)] via-transparent to-transparent" />
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-electric bg-navy-deep/70 px-2 py-1 rounded border border-electric/40">
                  <span className="pulse-dot" /> Live Operations
                </span>
                <DemoBadge />
              </div>
              <div className="absolute bottom-5 left-5 right-5">
                <div className="font-display text-xl text-cream">CyberWater Security Layer</div>
                <div className="data-line mt-2" />
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 glass-accent p-4 w-60 hidden xl:block">
              <div className="text-[10px] uppercase tracking-[0.26em] text-electric">Avg Device Trust</div>
              <div className="metric text-3xl text-gold">{summary?.avg_device_trust_score ?? "—"}</div>
              <div className="text-xs text-cream-dim">across water infrastructure</div>
            </div>
          </div>
        </div>
      </section>

      {/* FREE MARKETING AUDIT — LEAD MAGNET BANNER */}
      <section className="border-b border-gold-dim/30 bg-gradient-to-r from-navy-deep via-navy-soft to-navy-deep">
        <div className="container-wide py-12 sm:py-14">
          <Link to="/free-audit" className="block group" data-testid="free-audit-banner">
            <div className="glass p-6 sm:p-8 border-2 border-gold/40 hover:border-gold transition-colors flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
              <div>
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gold mb-2">
                  Sparkles · Free Audit · 30 seconds
                </div>
                <h2 className="font-display text-2xl sm:text-3xl text-cream">
                  Get your <span className="text-gold">Traction Score</span> — free, no signup.
                </h2>
                <p className="text-cream-dim text-sm mt-2 max-w-2xl">
                  Instant AI snapshot of your website's SEO health, conversion funnel, social
                  presence, and reputation. Type your URL — we'll do the rest.
                </p>
              </div>
              <div className="inline-flex items-center gap-2 text-gold font-mono text-sm group-hover:translate-x-1 transition-transform whitespace-nowrap">
                Run Free Audit <ArrowRight size={16} />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* KPIs */}
      <section className="border-y border-gold-dim/30 bg-navy-deep/40">
        <div className="container-wide py-10 sm:py-12 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5">
          <KPI label="Entities Tracked" value={summary?.entities_tracked ?? "—"} testid="kpi-entities" sublabel="Cities · Counties · Districts" />
          <KPI label="Risk Flags" value={summary?.risk_flags_total ?? "—"} accent="warning" testid="kpi-flags" sublabel="Across demo entities" />
          <KPI label="Recommendations" value={summary?.recommendations ?? "—"} testid="kpi-recs" sublabel="Infrastructure reallocation" />
          <KPI label="Protected Devices" value={summary?.water_devices ?? "—"} accent="success" testid="kpi-devices" sublabel="PLCs · Sensors · Controllers" />
        </div>
      </section>

      {/* FEATURE GRID */}
      <section className="container-wide py-16 sm:py-24">
        <div className="mb-10 sm:mb-12">
          <div className="text-[11px] tracking-[0.28em] uppercase text-gold mb-3">The Platform</div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-[2.25rem]">One platform. Every layer of accountability.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {[
            { I: IconBudget,        t: "Budget Scanner",        d: "AI-assisted discovery of adopted budgets, CIPs, and procurement records.",       demo: "city_budget_risk" },
            { I: IconInfrastructure,t: "Infrastructure Risk",   d: "Risk scoring for roads, drainage, water, and deferred-maintenance backlogs.",    demo: "infrastructure_failure" },
            { I: IconCyber,         t: "Cybersecurity Layer",   d: "24/7 AI monitor, device trust, incident queue — defensive only.",                demo: "cybersecurity_breach" },
            { I: IconWater,         t: "CyberWater",            d: "PLC, pump, valve, and AWG unit protection with multi-party control.",            demo: "water_system_failure" },
            { I: IconDataMonitor,   t: "Traffic Intelligence",  d: "Signal timing, crash corridor analysis, and school zone safety reviews.",        demo: "traffic_congestion_crisis" },
            { I: IconCompliance,    t: "Vendor & Procurement",  d: "Sole-source concentration, split-invoice detection, and off-contract review.",   demo: "vendor_fraud" },
            { I: IconAI,            t: "Marketing & Traction",  d: "SEO visibility, conversion funnel, reputation, and social traction scoring.",     demo: "marketing_traction_gap" },
            { I: IconGovernment,    t: "Public Transparency",   d: "Resident-facing scores and questions worth asking.",                             demo: "public_transparency_failure" },
          ].map(({ I, t, d, demo }) => (
            <Link
              key={t}
              to={`/portal/demo/${demo}`}
              className="panel-elev p-6 hover:border-gold transition-colors group cursor-pointer block"
              data-testid={`feature-${t.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <I size={28} />
              <div className="mt-4 font-display text-lg text-cream group-hover:text-gold transition-colors">{t}</div>
              <div className="mt-2 text-sm text-cream-dim leading-relaxed">{d}</div>
              <div className="mt-4 text-[11px] uppercase tracking-[0.22em] text-electric group-hover:text-gold inline-flex items-center gap-1">
                See demo preview <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SPLIT */}
      <section className="container-wide pb-16 sm:pb-24 grid lg:grid-cols-2 gap-6 lg:gap-10">
        <div className="panel-elev p-0 overflow-hidden">
          <img src={secImg.src} alt={secImg.alt} className="w-full aspect-[16/10] object-cover opacity-90" />
          <div className="p-8">
            <div className="text-[11px] tracking-[0.28em] uppercase text-gold mb-3">Government Intelligence</div>
            <h3 className="font-display text-2xl text-cream">Budget risk scoring for civic leaders</h3>
            <p className="mt-3 text-sm text-cream-dim leading-relaxed">
              Preliminary analysis on department growth, capital project escalation,
              vendor concentration, and underfunded infrastructure — framed as
              review-worthy indicators, never accusations.
            </p>
            <Link to="/budget-scanner"><Button className="btn-ghost-gold mt-6 h-10 px-5" data-testid="home-to-scanner">Explore Budget Scanner</Button></Link>
          </div>
        </div>
        <div className="panel-elev p-0 overflow-hidden">
          <img src={waterImg.src} alt={waterImg.alt} className="w-full aspect-[16/10] object-cover opacity-90" />
          <div className="p-8">
            <div className="text-[11px] tracking-[0.28em] uppercase text-gold mb-3">Operational Defense</div>
            <h3 className="font-display text-2xl text-cream">CyberWater for critical water infrastructure</h3>
            <p className="mt-3 text-sm text-cream-dim leading-relaxed">
              Protects atmospheric water generators, data-center cooling loops,
              PLCs, pumps, and valves with zero-trust device identity and
              multi-party command authorization.
            </p>
            <Link to="/cybersecurity"><Button className="btn-ghost-gold mt-6 h-10 px-5" data-testid="home-to-cyber">Explore CyberWater</Button></Link>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="border-t border-gold-dim/30 bg-navy-deep">
        <div className="container-wide py-12 sm:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-8">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-gold mb-3">Pilot Program</div>
            <h3 className="font-display text-2xl sm:text-3xl">Bring AuditIQ to your city, county, or facility.</h3>
            <p className="text-sm text-cream-dim mt-2 max-w-2xl">
              Pilot cohorts receive executive onboarding, a CyberWater readiness
              assessment, and a tailored transparency roadmap.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <Link to="/pricing" className="contents"><Button className="btn-ghost-gold h-12 px-6 w-full sm:w-auto" data-testid="cta-pricing">See Pilot Tiers</Button></Link>
            <Link to="/contact" className="contents"><Button className="btn-gold h-12 px-6 w-full sm:w-auto" data-testid="cta-contact">Start a Conversation</Button></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
